import { Card, Col, Divider, Flex, Row, Tooltip, Typography } from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import useFetch from "../../hooks/useFetch";
import { IndexContext } from "../../context/IndexContext";
import { Graph, Shape } from "@antv/x6";
import registerNode from "../../functions/single_line_diagram/registerNode";
import dayjs from "dayjs";
import { InfoCircleOutlined } from "@ant-design/icons";
const { Text, Title } = Typography;

function GeneralInfoCard({ SiteID = 0 }) {
  const [fetchWrapper] = useFetch();
  const { notificationApi } = useContext(IndexContext);
  const [deviceHealth, setDeviceHealth] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const abort_controller = new AbortController();
  const graphRef = useRef(null);
  const timerRef = useRef(null);

  function getDevicesHealth() {
    fetchWrapper({
      endpoint_url: "dashboard/getDeviceHealth",
      method: "GET",
      params: { SiteID: SiteID },
      abort_controller: abort_controller,
      onSuccess: (response) => {
        setDeviceHealth(response.data);
        setLastUpdate(dayjs());
      },
      onResponseError: (response) => {
        setDeviceHealth(null);
        notificationApi.error({
          message: response.message,
        });
      },
    });
  }

  function getPowerFlowNode() {
    fetchWrapper({
      endpoint_url: "dashboard/getPowerFlowNode",
      method: "GET",
      params: { SiteID: SiteID },
      abort_controller: abort_controller,
      onSuccess: (response) => {
        graphRef.current?.clearCells();
        graphRef.current?.addNodes(response.data.nodes);
        graphRef.current?.addEdges(response.data.edges);

        graphRef.current?.zoomToFit({
          padding: { left: 40, top: 20, right: 40, bottom: 20 },
          maxScale: 1.1,
        });

        graphRef.current?.on("node:change:data", ({ node, current }) => {
          node.setAttrByPath(
            "active-power/text",
            `${current["power"].toFixed(2)} kW`
          );

          node.setAttrByPath(
            "indicator/fill",
            current["status"] == "Danger"
              ? "var(--ant-color-error)"
              : current["status"] == "Warning"
              ? "var(--ant-color-warning)"
              : current["status"] == "Online"
              ? "var(--ant-color-success)"
              : "var(--ant-color-text-disabled)"
          );
        });
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
      },
    });
  }

  function getPowerFlow() {
    fetchWrapper({
      endpoint_url: "dashboard/getPowerFlow",
      method: "GET",
      params: { SiteID: SiteID },
      abort_controller: abort_controller,
      onSuccess: (response) => {
        const data = response.data;

        data.forEach((nodeData) => {
          const node = graphRef.current?.getCellById(nodeData["node-id"]);

          node?.setData(nodeData["data"], { overwrite: true });
        });
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
      },
    });
  }

  useEffect(() => {
    getDevicesHealth();
    getPowerFlowNode();

    timerRef.current = setInterval(() => {
      getDevicesHealth();
      getPowerFlow();
    }, 10000);

    return () => {
      clearInterval(timerRef.current);
    };
  }, [SiteID]);

  useEffect(() => {
    registerNode();

    const graphContainer = document.getElementById("power-flow-diagram");
    const graph = new Graph({
      container: graphContainer,
      connecting: {
        router: {
          name: "manhattan",
          args: {
            padding: 65,
          },
        },
        connector: {
          name: "rounded",
          args: {
            radius: 3,
          },
        },
        anchor: "center",
        connectionPoint: "anchor",
        allowBlank: false,
        snap: {
          radius: 1,
        },
        createEdge() {
          return new Shape.Edge({
            shape: "edge",
            attrs: {
              line: {
                stroke: "var(--ant-color-text-base)",
                strokeDasharray: 0,
                style: {
                  animation: "ant-line 30s infinite linear",
                },
                strokeWidth: 0.5,
                sourceMarker: false,
                targetMarker: false,
              },
            },
            zIndex: 0,
          });
        },
        validateConnection({ targetMagnet }) {
          return !!targetMagnet;
        },
      },
      interacting: false,
    });
    graphRef.current = graph;

    const resizeObserver = new ResizeObserver(() => {
      graphRef.current?.resize(
        graphContainer.parentElement.offsetWidth - 10,
        graphContainer.parentElement.offsetHeight
      );
      graphRef.current?.zoomToFit({
        padding: { left: 40, top: 20, right: 40, bottom: 20 },
        maxScale: 1.1,
      });
    });

    resizeObserver.observe(graphContainer.parentElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Card size="small" className="h-100">
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Flex justify="center" align="center" gap={4} vertical={false}>
            <Tooltip
              title={
                `Last Update On : ${lastUpdate?.format(
                  "YYYY-MM-DD HH:mm:ss"
                )}` ?? "-"
              }
            >
              <Text strong>DEVICE HEALTH</Text>
            </Tooltip>
          </Flex>
          <Flex justify="center" align="center" gap={4} vertical={false}>
            <Text></Text>
          </Flex>
        </Col>
        <Col span={24}>
          <Flex
            justify="space-evenly"
            align="center"
            style={{ textAlign: "center" }}
          >
            <div style={{ flex: "1 0 15%", overflow: "hidden" }}>
              <Title
                level={3}
                style={{ marginTop: 12 }}
                ellipsis={{
                  tooltip: deviceHealth?.total?.toLocaleString() ?? "-",
                }}
              >
                {deviceHealth?.total?.toLocaleString() ?? "-"}
                <Title level={5} style={{ marginTop: 12 }}>
                  Total
                </Title>
              </Title>
            </div>
            <Divider type="vertical" style={{ height: 60, borderWidth: 2 }} />
            <div style={{ flex: "1 0 15%", overflow: "hidden" }}>
              <Title
                level={3}
                style={{ marginTop: 12 }}
                ellipsis={{
                  tooltip: deviceHealth?.online?.toLocaleString() ?? "-",
                }}
              >
                {deviceHealth?.online?.toLocaleString() ?? "-"}
                <Title level={5} style={{ marginTop: 12 }}>
                  Online
                </Title>
              </Title>
            </div>
            <Divider type="vertical" style={{ height: 60, borderWidth: 2 }} />
            <div style={{ flex: "1 0 15%", overflow: "hidden" }}>
              <Title
                level={3}
                style={{ marginTop: 12 }}
                ellipsis={{
                  tooltip: deviceHealth?.offline?.toLocaleString() ?? "-",
                }}
              >
                {deviceHealth?.offline?.toLocaleString() ?? "-"}
                <Title level={5} style={{ marginTop: 12 }}>
                  Offline
                </Title>
              </Title>
            </div>
            <Divider type="vertical" style={{ height: 60, borderWidth: 2 }} />
            <div style={{ flex: "1 0 15%", overflow: "hidden" }}>
              <Title
                level={3}
                style={{ marginTop: 12 }}
                ellipsis={{
                  tooltip: deviceHealth?.warning?.toLocaleString() ?? "-",
                }}
              >
                {deviceHealth?.warning?.toLocaleString() ?? "-"}
                <Title level={5} style={{ marginTop: 12 }}>
                  Warning
                </Title>
              </Title>
            </div>
            <Divider type="vertical" style={{ height: 60, borderWidth: 2 }} />
            <div style={{ flex: "1 0 15%", overflow: "hidden" }}>
              <Title
                level={3}
                style={{ marginTop: 12 }}
                ellipsis={{
                  tooltip: deviceHealth?.danger?.toLocaleString() ?? "-",
                }}
              >
                {deviceHealth?.danger?.toLocaleString() ?? "-"}
                <Title level={5} style={{ marginTop: 12 }}>
                  Danger
                </Title>
              </Title>
            </div>
          </Flex>
          {/* <Flex justify="center" align="center" gap={4} vertical={false}>
            <Text>
              Last Update On :{" "}
              {lastUpdate?.format("YYYY-MM-DD HH:mm:ss") ?? "-"}
            </Text>
          </Flex> */}
        </Col>
        <Col span={24}>
          <Flex justify="space-evenly" align="center">
            <Text strong>POWER FLOW OVERVIEW</Text>
          </Flex>
        </Col>
        <Col
          span={24}
          className="w-100"
          style={{ height: 375, overflow: "hidden" }}
        >
          <div id="power-flow-diagram" className="w-100 h-100"></div>
          {/* <CustomG6Diagram data={realtime_power_flow_data} /> */}
        </Col>
      </Row>
    </Card>
  );
}

export default GeneralInfoCard;
